'use client';

import { AtomicModel } from '@/data/atomHistory';
import { motion } from 'framer-motion';
import { Atom, CheckCircle2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';

interface AtomicModelCardProps {
  model: AtomicModel;
  index: number;
}

export function AtomicModelCard({ model, index }: AtomicModelCardProps) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      whileInView={{ opacity: 1, scale: 1 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: index * 0.15 }}
    >
      <motion.div
        whileHover={{ y: -10 }}
        transition={{ duration: 0.3 }}
      >
        <Card className="h-full dark:bg-gradient-to-br dark:from-[#112240] dark:to-[#0a192f] light:from-white light:to-gray-50 dark:border-[#233554] light:border-gray-200">
          <CardHeader>
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <CardTitle className="flex items-center gap-2 dark:text-[#64ffda] light:text-teal-600 mb-2">
                  <Atom className="w-5 h-5" />
                  {model.name}
                </CardTitle>
                <p className="text-sm dark:text-[#a8b2d1] light:text-gray-600">
                  {model.scientist}
                </p>
              </div>
              <Badge variant="outline" className="dark:border-[#64ffda] dark:text-[#64ffda] light:border-teal-600 light:text-teal-600">
                {model.year}
              </Badge>
            </div>
          </CardHeader>

          <CardContent className="space-y-4">
            {/* Description */}
            <p className="text-sm dark:text-[#ccd6f6] light:text-gray-700 leading-relaxed">
              {model.description}
            </p>

            {/* Characteristics */}
            <div className="space-y-2">
              <p className="text-xs font-semibold dark:text-[#64ffda] light:text-teal-600 uppercase tracking-wide">
                Caractéristiques clés
              </p>
              <ul className="space-y-2">
                {model.characteristics.map((char, idx) => (
                  <motion.li
                    key={idx}
                    initial={{ opacity: 0, x: -10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.15 + idx * 0.1 }}
                    className="flex items-start gap-2 text-sm dark:text-[#a8b2d1] light:text-gray-600"
                  >
                    <CheckCircle2 className="w-4 h-4 dark:text-[#64ffda] light:text-teal-600 flex-shrink-0 mt-0.5" />
                    <span>{char}</span>
                  </motion.li>
                ))}
              </ul>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </motion.div>
  );
}
